import { createSelector } from "@reduxjs/toolkit";

import type {
    Curation,
    HydratedCurationModule,
    HydratedModulesById,
} from "~types/types";
import get from "~utils/get";
import getComponentName from "~utils/getComponentName";

export const modulesSelector = (state: { modulesById: HydratedModulesById }) =>
    state.modulesById;
export const curationSelector = (state: { curation: Curation }) =>
    state.curation;

const hasMedia = (module: HydratedCurationModule | null): boolean => {
    if (!module) {
        return false;
    }
    if (get(module, "image")) return true;

    if (get(module, "items.0.image")) return true;

    // I included video, because video modules usually take up the viewport above the fold on load
    // We don't want to inadvertly eagerly load a module Image below the fold of a HFS Video above the fold
    if (get(module, "video")) return true;

    return false;
};

export const selectPriorityModule = createSelector(
    modulesSelector,
    (modules) => {
        if (!modules) return undefined;
        // These modules usually have images but are either hidden on mobile usually,
        // or have thumbnail images, which won't ever be the LCP element, so ignore them
        const IGNORE = /storylist|sectionfront|bannerrecirc/;

        const moduleId = Object.keys(modules).find((idx) => {
            if (!modules[idx]?.variation || !modules[idx]?.type) {
                return false;
            }
            const componentName = getComponentName(
                modules[idx].variation,
                modules[idx].type
            )?.toLowerCase();

            return !IGNORE.test(componentName) && hasMedia(modules[idx]);
        }) as string;

        return getComponentName(
            modules[moduleId]?.variation as string,
            modules[moduleId]?.type as string
        );
    }
);

export const isPrioritySelector = createSelector(
    selectPriorityModule,
    (_, componentName: string) => componentName,
    (priorityModule, componentName) => {
        return priorityModule === componentName;
    }
);
